import { useEffect, useMemo, useState } from "react";
import { useLeague } from "./LeagueLayout";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import SponsorCarousel from "../../components/SponsorCarousel";
import SponsorBetDialog from "../../components/SponsorBetDialog";
import { Sparkles, Zap, Trophy, ShieldAlert, Info, History, Coins, Target, X, Settings } from "lucide-react";

/**
 * Pagina Sponsor lato MANAGER.
 * Flow:
 *  1. Carosello Bonus + Carosello Pagato → manager sceglie 2 sponsor (uno per categoria)
 *  2. Sponsor scelti appaiono nella sezione "I miei sponsor"
 *  3. Da lì può premere "🎯 Scommetti su questo sponsor" (min 30, raddoppia se obiettivo centrato)
 */
export default function LeagueSponsor() {
  const { league, myTeam, isAdmin } = useLeague();
  const [sponsors, setSponsors] = useState([]);
  const [settings, setSettings] = useState({ bonus_enabled: true, paid_enabled: true });
  const [mySponsorships, setMySponsorships] = useState([]);
  const [teamBudget, setTeamBudget] = useState(myTeam?.budget ?? 0);
  const [loading, setLoading] = useState(true);
  const [betTarget, setBetTarget] = useState(null); // sponsorship object

  const loadAll = async () => {
    setLoading(true);
    try {
      const [sList, sSet] = await Promise.all([
        api.get(`/leagues/${league.league_id}/sponsors`, { params: { active_only: true } }),
        api.get(`/leagues/${league.league_id}/sponsors/settings`),
      ]);
      setSponsors(sList.data || []);
      setSettings(sSet.data || { bonus_enabled: true, paid_enabled: true });
      if (myTeam) {
        const [tsp, teamR] = await Promise.all([
          api.get(`/leagues/${league.league_id}/teams/${myTeam.team_id}/sponsorships`),
          api.get(`/leagues/${league.league_id}/teams/${myTeam.team_id}`),
        ]);
        setMySponsorships((tsp.data || []).filter(s => s.status === "active"));
        if (teamR.data?.budget !== undefined) setTeamBudget(Number(teamR.data.budget || 0));
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadAll(); }, [league.league_id, myTeam?.team_id]);

  // Iter 67: sponsor DEVONO avere una descrizione dell'obiettivo per essere mostrati.
  // Rimuove doppioni non spiegati (bandi vecchi/incompleti).
  const hasObjective = (s) => Boolean((s.objective_desc || "").trim());

  const bonusList = useMemo(
    () => (sponsors || []).filter(s => s.category === "bonus_initial" && s.active !== false && hasObjective(s)),
    [sponsors]
  );
  const paidList = useMemo(
    () => (sponsors || []).filter(s => s.category === "manager_paid" && s.active !== false && hasObjective(s)),
    [sponsors]
  );

  const bonusChoice = mySponsorships.find(s => s.category === "bonus_initial") || null;
  const paidChoice = mySponsorships.find(s => s.category === "manager_paid") || null;
  // Vincolo: i 2 sponsor devono essere DIVERSI. Filtro fuori dal carosello dell'altra categoria
  // l'eventuale sponsor con lo stesso NAME già scelto.
  const excludedFromPaid = bonusChoice ? new Set([bonusChoice.sponsor_name]) : new Set();
  const excludedFromBonus = paidChoice ? new Set([paidChoice.sponsor_name]) : new Set();
  const bonusListFiltered = bonusList.filter(s => !excludedFromBonus.has(s.name));
  const paidListFiltered = paidList.filter(s => !excludedFromPaid.has(s.name));
  const anyBet = mySponsorships.some(s => s.bet_amount > 0);
  const totalBet = mySponsorships.reduce((sum, s) => sum + (s.bet_amount || 0), 0);
  const maxWinFromBet = totalBet * 2;
  const totalPrize = mySponsorships.reduce((sum, s) => sum + (s.prize_credits || 0), 0);
  const maxPrize = mySponsorships.reduce((sum, s) => sum + Math.round((s.prize_credits || 0) * 1.5), 0);

  const phase = league.season_phase ?? 3;
  const isPhase1 = phase === 1;

  if (loading) return <div className="text-slate-400 text-sm">Caricamento sponsor…</div>;

  return (
    <div className="space-y-6" data-testid="league-sponsor-page">
      {/* Hero */}
      <div className="rounded-3xl border border-amber-500/30 bg-gradient-to-br from-slate-900 via-slate-950 to-amber-950/20 p-6 sm:p-7 relative overflow-hidden">
        <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-amber-500/10 blur-3xl pointer-events-none"/>
        <div className="absolute -bottom-16 -left-16 w-64 h-64 rounded-full bg-emerald-500/10 blur-3xl pointer-events-none"/>
        <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="flex items-center gap-2 shrink-0">
            <Sparkles className="text-amber-400" size={22}/>
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-white">Sponsor</h1>
          </div>
          <div className="flex-1 text-sm text-slate-300 leading-relaxed">
            Scegli <span className="font-black text-white">2 sponsor</span>: un <span className="text-emerald-300 font-bold">Bonus</span> (crediti subito) e un <span className="text-amber-300 font-bold">Pagato</span> (paghi ora per premi maggiori). Una volta scelti, puoi <span className="text-amber-300 font-black">scommettere</span> crediti tuoi su uno di loro (min 30) — se centra l&apos;obiettivo <span className="text-emerald-300 font-black">raddoppiano</span>.
          </div>
          <Link to={`/lega/${league.league_id}/sponsor/storico`}
            data-testid="link-sponsor-history"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-[0.15em] bg-slate-800/80 border border-slate-700 text-slate-200 hover:text-white hover:border-slate-500 shrink-0">
            <History size={13}/> Storico
          </Link>
        </div>
      </div>

      {!myTeam && (
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5 text-slate-300 text-sm flex items-start gap-2" data-testid="no-team-warning">
          <Info size={16} className="text-amber-400 mt-0.5 shrink-0"/>
          <div>Non hai ancora una squadra assegnata. Reclama una squadra libera dalla <span className="font-bold">Dashboard</span> per accedere alla scelta sponsor.</div>
        </div>
      )}

      {/* Riassunto scelte del team */}
      {myTeam && (
        <div className="grid sm:grid-cols-4 gap-3">
          <StatCard label="Budget team" value={`${Math.floor(teamBudget)}c`} tone="slate"/>
          <StatCard label="Sponsor scelti" value={`${mySponsorships.length}/2`} tone={mySponsorships.length === 2 ? "emerald" : "amber"}/>
          <StatCard label="Scommessa attiva" value={anyBet ? `${totalBet}c → 2×` : "—"} tone={anyBet ? "amber" : "slate"} icon={<Zap size={13}/>}/>
          <StatCard label="Premio max possibile" value={`${maxPrize + maxWinFromBet}c`} tone="emerald" icon={<Trophy size={13}/>}/>
        </div>
      )}

      {/* I MIEI SPONSOR — sezione con bet button */}
      {myTeam && mySponsorships.length > 0 && (
        <div className="space-y-3" data-testid="my-sponsors-section">
          <div className="flex items-center gap-2">
            <div className="h-px flex-1 bg-slate-800"/>
            <h2 className="text-xs font-black uppercase tracking-[0.3em] text-slate-400 px-3">
              I miei sponsor
            </h2>
            <div className="h-px flex-1 bg-slate-800"/>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {mySponsorships.map(sp => (
              <MySponsorCard key={sp.sponsorship_id} sp={sp}
                otherHasBet={mySponsorships.some(o => o.sponsorship_id !== sp.sponsorship_id && o.bet_amount > 0)}
                isPhase1={isPhase1}
                onBetClick={() => setBetTarget(sp)}/>
            ))}
          </div>
        </div>
      )}

      {/* Fase != 1 → mostro avviso */}
      {myTeam && !isPhase1 && (
        <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-4 flex items-start gap-2 text-sm text-slate-300" data-testid="phase-lock-warning">
          <ShieldAlert size={16} className="text-amber-400 mt-0.5 shrink-0"/>
          <div>La scelta sponsor e le scommesse sono disponibili solo in <span className="font-bold text-white">Fase 1 · Import Rose</span>. Attuale: fase {phase}.</div>
        </div>
      )}

      {/* Caroselli */}
      {myTeam && (
        <>
          <div className="flex items-center gap-2">
            <div className="h-px flex-1 bg-slate-800"/>
            <h2 className="text-xs font-black uppercase tracking-[0.3em] text-slate-400 px-3">
              {mySponsorships.length === 2 ? "Modifica scelta" : "Scegli i tuoi 2 sponsor"}
            </h2>
            <div className="h-px flex-1 bg-slate-800"/>
          </div>
          <div className="grid lg:grid-cols-2 gap-5">
            {settings.bonus_enabled !== false ? (
              <SponsorCarousel
                leagueId={league.league_id}
                teamId={myTeam.team_id}
                category="bonus_initial"
                sponsors={bonusListFiltered}
                currentChoice={bonusChoice}
                currentBudget={teamBudget}
                isPhase1={isPhase1}
                onChanged={loadAll}
              />
            ) : (
              <div className="rounded-3xl border border-dashed border-slate-700 bg-slate-900/40 p-8 text-slate-400 text-xs text-center" data-testid="bonus-disabled">
                Il carosello Sponsor Bonus è disattivato dall&apos;admin.
              </div>
            )}
            {settings.paid_enabled !== false ? (
              <SponsorCarousel
                leagueId={league.league_id}
                teamId={myTeam.team_id}
                category="manager_paid"
                sponsors={paidListFiltered}
                currentChoice={paidChoice}
                currentBudget={teamBudget}
                isPhase1={isPhase1}
                onChanged={loadAll}
              />
            ) : (
              <div className="rounded-3xl border border-dashed border-slate-700 bg-slate-900/40 p-8 text-slate-400 text-xs text-center" data-testid="paid-disabled">
                Il carosello Sponsor Pagato è disattivato dall&apos;admin.
              </div>
            )}
          </div>
        </>
      )}

      {/* Link discreto per admin */}
      {isAdmin && (
        <div className="pt-4 flex justify-end">
          <Link to={`/lega/${league.league_id}/decisioni`}
            data-testid="link-admin-decisioni"
            className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500 hover:text-amber-300">
            <Settings size={12}/> Gestisci come admin →
          </Link>
        </div>
      )}

      {/* Bet Dialog */}
      {betTarget && (
        <SponsorBetDialog
          leagueId={league.league_id}
          teamId={myTeam.team_id}
          sponsorship={betTarget}
          otherHasBet={mySponsorships.some(o => o.sponsorship_id !== betTarget.sponsorship_id && o.bet_amount > 0)}
          currentBudget={teamBudget}
          onClose={() => setBetTarget(null)}
          onDone={loadAll}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, tone = "slate", icon = null }) {
  const toneMap = {
    slate: "border-slate-800 bg-slate-900/60 text-slate-100",
    emerald: "border-emerald-500/30 bg-emerald-500/10 text-emerald-100",
    amber: "border-amber-500/30 bg-amber-500/10 text-amber-100",
  };
  return (
    <div className={`rounded-2xl border p-3.5 ${toneMap[tone]}`}>
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-400 flex items-center gap-1">{icon}{label}</div>
      <div className="mt-1.5 text-2xl font-black tabular-nums tracking-tight">{value}</div>
    </div>
  );
}

function MySponsorCard({ sp, otherHasBet, isPhase1, onBetClick }) {
  const isBonus = sp.category === "bonus_initial";
  const accent = isBonus ? "#10b981" : "#f59e0b";
  const softBg = isBonus ? "rgba(16,185,129,0.10)" : "rgba(245,158,11,0.10)";
  const softBorder = isBonus ? "rgba(16,185,129,0.35)" : "rgba(245,158,11,0.35)";
  const hasBet = Number(sp.bet_amount || 0) > 0;

  return (
    <div className="rounded-2xl border p-4 relative overflow-hidden flex items-center gap-3"
      style={{ background: `radial-gradient(600px 200px at 100% -50px, ${softBg}, transparent 60%), #0b1220`, borderColor: softBorder }}
      data-testid={`my-sponsor-${sp.category}`}>
      {sp.sponsor_logo_url && (
        <img src={sp.sponsor_logo_url} alt={sp.sponsor_name}
          className="w-20 h-20 object-contain shrink-0 drop-shadow-lg"
          style={{ filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.4))" }}/>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-black text-white truncate">{sp.sponsor_name}</span>
          <span className="text-[9px] uppercase tracking-[0.2em] font-bold px-1.5 py-0.5 rounded"
            style={{ color: accent, background: softBg, border: `1px solid ${softBorder}` }}>
            {isBonus ? "Bonus" : "Pagato"}
          </span>
        </div>
        {sp.objective_desc && (
          <div className="text-xs text-slate-300 flex items-start gap-1.5 mt-1 leading-snug">
            <Target size={11} className="mt-0.5 shrink-0" style={{ color: accent }}/>
            <span>{sp.objective_desc}</span>
          </div>
        )}
        <div className="flex items-center gap-2 mt-2.5 flex-wrap">
          <div className="text-sm px-2.5 py-1 rounded-lg bg-slate-900 border border-slate-700 text-slate-300 font-mono flex items-center gap-1">
            <Trophy size={11} style={{ color: accent }}/> Base <span className="font-black text-base" style={{ color: accent }}>{sp.prize_credits}c</span>
            <span className="text-[9px] text-slate-500 ml-1">
              ({Math.round(sp.prize_credits * (sp.default_percent || 50) / 100)}→{Math.round(sp.prize_credits * 1.5)}c)
            </span>
          </div>
          {hasBet ? (
            <button onClick={onBetClick}
              data-testid={`bet-edit-${sp.category}`}
              disabled={!isPhase1}
              className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 border border-amber-500/50 text-amber-300 font-black uppercase tracking-widest hover:bg-amber-500/30 disabled:opacity-40">
              <Zap size={10}/> Bet {sp.bet_amount}c → 2×
            </button>
          ) : (
            <button onClick={onBetClick}
              data-testid={`bet-add-${sp.category}`}
              disabled={!isPhase1 || (otherHasBet)}
              className="inline-flex items-center gap-1 text-[10px] px-2.5 py-1 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 text-slate-900 font-black uppercase tracking-widest hover:brightness-110 disabled:opacity-40 disabled:from-slate-700 disabled:to-slate-800 disabled:text-slate-500 shadow-md">
              <Zap size={10}/> Scommetti su questo sponsor
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
